import { Audio, Sequence, useCurrentFrame, useVideoConfig } from 'remotion'
import { isImageOnlyClip, type TimelineEntry, type TimelineModel } from '@/lib/clipUtils'
import type { AudioTrackInput, AudioMixState } from '../types'
import { secondsToFrames, rangeToFrames } from '../utils/framemath'

interface ExtraAudioRendererProps {
  audioTracks: AudioTrackInput[]
  audioMix?: AudioMixState
  pauseWhenBuffering?: boolean
  acceptableTimeShiftInSeconds?: number
}

/** Linearly interpolate volume from sorted envelope points.
 *  time is relative to track start (seconds). */
function sampleEnvelope(envelope: { time: number; volume: number }[], time: number): number {
  if (envelope.length === 0) return 1
  if (time <= envelope[0].time) return envelope[0].volume
  if (time >= envelope[envelope.length - 1].time) return envelope[envelope.length - 1].volume
  for (let i = 0; i < envelope.length - 1; i++) {
    const a = envelope[i], b = envelope[i + 1]
    if (time >= a.time && time <= b.time) {
      const t = (time - a.time) / (b.time - a.time)
      return a.volume + (b.volume - a.volume) * t
    }
  }
  return envelope[envelope.length - 1].volume
}

/** Music beds loop so a track whose duration was extended past its source
 *  length (ISS-720) fills with repeated audio instead of trailing silence.
 *  Volume must be computed from the sequence-local frame here: with `loop`,
 *  Remotion restarts the volume-callback frame on every iteration, which
 *  would re-trigger fade-ins and replay the envelope from 0. */
function LoopingTrackAudio({
  src,
  trimStartFrames,
  volumeAtFrame,
  pauseWhenBuffering,
  acceptableTimeShiftInSeconds,
}: {
  src: string
  trimStartFrames: number
  volumeAtFrame: (frame: number) => number
  pauseWhenBuffering: boolean
  acceptableTimeShiftInSeconds: number
}) {
  const frame = useCurrentFrame()
  return (
    <Audio
      loop
      src={src}
      startFrom={trimStartFrames}
      volume={volumeAtFrame(frame)}
      // Without onError, a looped track whose src fails to load cancels the
      // whole render; warn and keep exporting, matching non-loop behavior.
      onError={err => console.warn(`Music track failed to load, continuing without it: ${err.message}`)}
      pauseWhenBuffering={pauseWhenBuffering}
      acceptableTimeShiftInSeconds={acceptableTimeShiftInSeconds}
    />
  )
}

export function ExtraAudioRenderer({
  audioTracks,
  audioMix,
  pauseWhenBuffering = true,
  acceptableTimeShiftInSeconds = 1.2,
}: ExtraAudioRendererProps) {
  const { fps } = useVideoConfig()

  return (
    <>
      {audioTracks.map(track => {
        const muteKey = track.type === 'voiceover' ? 'voice' : track.type
        const trackMuted = audioMix?.mutedTracks[muteKey] || audioMix?.mutedTracks[track.id]
        if (trackMuted) return null

        const trackVolume = audioMix?.trackVolumes[muteKey] ?? audioMix?.trackVolumes[track.id] ?? 1
        const perTrackVol = audioMix?.clipVolumes[track.id] ?? track.volume
        const baseVolume = perTrackVol * trackVolume
        if (baseVolume === 0 && !track.volumeEnvelope) return null

        const { startFrame, durationFrames } = rangeToFrames(track.startTime, track.startTime + track.duration, fps)
        const trimStartFrames = secondsToFrames(track.trimStart || 0, fps)

        const fadeInFrames = track.fadeIn ? secondsToFrames(track.fadeIn, fps) : 0
        const fadeOutFrames = track.fadeOut ? secondsToFrames(track.fadeOut, fps) : 0
        const envelope = track.volumeEnvelope

        const volumeCallback = (f: number) => {
          // Volume envelope automation — interpolate between keyframe points
          let vol: number
          if (envelope && envelope.length > 0) {
            const trackTime = f / fps  // seconds into the track
            vol = sampleEnvelope(envelope, trackTime) * trackVolume
          } else {
            vol = baseVolume
          }

          if (fadeInFrames > 0 && f < fadeInFrames) {
            vol *= f / fadeInFrames
          }
          if (fadeOutFrames > 0 && f > durationFrames - fadeOutFrames) {
            vol *= (durationFrames - f) / fadeOutFrames
          }
          return Math.max(0, Math.min(1, vol))
        }

        return (
          <Sequence key={`extra-audio-${track.id}`} from={startFrame} durationInFrames={durationFrames}>
            {track.type === 'music' ? (
              <LoopingTrackAudio
                src={track.url}
                trimStartFrames={trimStartFrames}
                volumeAtFrame={volumeCallback}
                pauseWhenBuffering={pauseWhenBuffering}
                acceptableTimeShiftInSeconds={acceptableTimeShiftInSeconds}
              />
            ) : (
              <Audio
                src={track.url}
                startFrom={trimStartFrames}
                volume={volumeCallback}
                pauseWhenBuffering={pauseWhenBuffering}
                acceptableTimeShiftInSeconds={acceptableTimeShiftInSeconds}
              />
            )}
          </Sequence>
        )
      })}
    </>
  )
}

function hasClipNativeAudio(entry: TimelineEntry) {
  const { clip } = entry
  if (!clip.videoUrl || clip.videoStatus !== 'completed' || isImageOnlyClip(clip)) return false
  if (clip.nativeAudioEnabled === false) return false
  return Boolean(
    clip.nativeAudioEnabled ||
    clip.generationMode === 'avatar' ||
    clip.transcription?.words?.length
  )
}

function getClipAudioVolume(entry: TimelineEntry, audioMix?: AudioMixState) {
  const { clip } = entry
  if (clip.nativeAudioEnabled === false) return 0
  if (audioMix?.mutedTracks['videoAudio']) return 0
  if (audioMix?.clipMutes[clip._id]) return 0
  const trackVol = audioMix?.trackVolumes['videoAudio'] ?? 1
  const clipVol = audioMix?.clipVolumes[clip._id] ?? 1
  return Math.max(0, Math.min(1, trackVol * clipVol))
}

interface ClipAudioRendererProps {
  model: TimelineModel
  audioMix?: AudioMixState
  pauseWhenBuffering?: boolean
  acceptableTimeShiftInSeconds?: number
}

export function ClipAudioRenderer({
  model,
  audioMix,
  pauseWhenBuffering = false,
  acceptableTimeShiftInSeconds = 1.2,
}: ClipAudioRendererProps) {
  const { fps } = useVideoConfig()

  return (
    <>
      {model.entries.map((entry) => {
        if (!hasClipNativeAudio(entry)) return null

        const volume = getClipAudioVolume(entry, audioMix)
        if (volume <= 0) return null

        const { startFrame, durationFrames } = rangeToFrames(
          entry.globalStart,
          entry.globalEnd,
          fps,
        )
        const trimStartFrames = secondsToFrames(entry.clip.trimStart || 0, fps)

        return (
          <Sequence
            key={`clip-audio-${entry.clip._id}`}
            from={startFrame}
            durationInFrames={durationFrames}
          >
            <Audio
              src={entry.clip.videoUrl!}
              startFrom={trimStartFrames}
              volume={volume}
              pauseWhenBuffering={pauseWhenBuffering}
              acceptableTimeShiftInSeconds={acceptableTimeShiftInSeconds}
            />
          </Sequence>
        )
      })}
    </>
  )
}
